
import React, { useContext, useCallback } from 'react';
import _ from 'lodash';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import { MLAvailablePolicyCoveragesService, MLAvailableVehicleCoveragesService, MLAvailablePropertyCoveragesService } from 'gw-capability-claim';

import metadata from './MLClaimDetailsCoverages.metadata.json5';
import messages from './MLClaimDetailsCoverages.messages';

import { IntlContext } from '@jutro/legacy/locale';

function MLClaimDetailsCoverages(props) {
    const {
        claim,
        isPersonalAuto,
        insuredVehicleIncident
    } = props;
    const translator = useTranslator();
    const intl = useContext(IntlContext)

    const formatCurrency = (value) => {
        let formattedAmount = '';
        if (value) {
            const { amount, currency } = value;
            if (!_.isUndefined(amount) && currency) {
                formattedAmount = intl.formatNumber(amount, { style: 'currency', currency: currency });
            }
        }
        return formattedAmount;
    };
    
    const filteredPolicyCoverages = useCallback(() => {
        const filteredCoverages = claim?.policy?.coverages && claim.policy.coverages.filter((coverage) => {
            let policyCoverage;
            if (coverage.exposureLimit || coverage.incidentLimit || coverage.deductible) {
                policyCoverage = MLAvailablePolicyCoveragesService.getCoverage(coverage.type_ML);
                if (policyCoverage) {
                    coverage.name = policyCoverage.name;
                }
            }
            return !_.isUndefined(policyCoverage);
        });
        return filteredCoverages || [];
    }, [claim]);

    const filteredVehicleCoverages = useCallback(() => {
        const filteredCoverages = insuredVehicleIncident?.vehicle?.coverages && insuredVehicleIncident.vehicle.coverages.filter((coverage) => {
            let policyCoverage;
            if (coverage.exposureLimit || coverage.incidentLimit || coverage.deductible) {
                policyCoverage = MLAvailableVehicleCoveragesService.getCoverage(coverage.type_ML);
                if (policyCoverage) {
                    coverage.name = policyCoverage.name;
                }
            }
            return !_.isUndefined(policyCoverage);
        });
        return filteredCoverages || [];
    }, [insuredVehicleIncident]);

    const filteredPropertyCoverages = useCallback(() => {
        const lossLocation = claim?.policy?.lobs?.homeowner?.locations?.length > 0 && claim.policy.lobs.homeowner.locations[0];
        const filteredCoverages = lossLocation?.coverages && lossLocation.coverages.filter((coverage) => {
            let policyCoverage;
            if (coverage.exposureLimit || coverage.incidentLimit || coverage.deductible) {
                policyCoverage = MLAvailablePropertyCoveragesService.getCoverage(coverage.type_ML);
                if (policyCoverage) {
                    coverage.name = policyCoverage.name;
                }
            }
            return !_.isUndefined(policyCoverage);
        });
        return filteredCoverages || [];
    }, [claim]);

    const isPolicyCoverage = useCallback(() => {
        return filteredPolicyCoverages().length > 0;
    }, [filteredPolicyCoverages]);    

    const generatePolicyOverrides = () => {
        const policyCoverageOverride = filteredPolicyCoverages().map((policyCoverage, index) => {
            return {
                [`policyCoverageExposureAndIncident${index}`]: {
                    visible: !!(policyCoverage.exposureLimit && policyCoverage.incidentLimit),
                    content: `${formatCurrency(policyCoverage.exposureLimit)}/${formatCurrency(policyCoverage.incidentLimit)}`
                },
                [`policyCoverageNoExpusureAndIncident${index}`]: {
                    visible: !!(!policyCoverage.exposureLimit && policyCoverage.incidentLimit),
                    content: `${formatCurrency(policyCoverage.incidentLimit)}`
                },
                [`policyCoverageExposureAndNoIncident${index}`]: {
                    visible: !!(policyCoverage.exposureLimit && !policyCoverage.incidentLimit),
                    content: `${formatCurrency(policyCoverage.exposureLimit)}`
                },
                [`policyCoverageDeductible${index}`]: {
                    visible: !!policyCoverage.deductible,
                    content: `${formatCurrency(policyCoverage.deductible)}`
                },
            };
        });

        return Object.assign({}, ...policyCoverageOverride);
    };

    const generateVehicleOverrides = () => {
        const vehicleCoverageOverride = filteredVehicleCoverages().map((vehicleCoverage, index) => {
            return {
                [`vehicleCoverageExposureAndIncident${index}`]: {
                    visible: !!(vehicleCoverage.exposureLimit && vehicleCoverage.incidentLimit),
                    content: `${formatCurrency(vehicleCoverage.exposureLimit)}/${formatCurrency(vehicleCoverage.incidentLimit)}`
                },
                [`vehicleCoverageNoExpusureAndIncident${index}`]: {
                    visible: !!(!vehicleCoverage.exposureLimit && vehicleCoverage.incidentLimit),
                    content: `${formatCurrency(vehicleCoverage.incidentLimit)}`
                },
                [`vehicleCoverageExposureAndNoIncident${index}`]: {
                    visible: !!(vehicleCoverage.exposureLimit && !vehicleCoverage.incidentLimit),
                    content: `${formatCurrency(vehicleCoverage.exposureLimit)}`
                },
                [`vehicleCoverageDeductible${index}`]: {
                    visible: !!vehicleCoverage.deductible,
                    content: `${formatCurrency(vehicleCoverage.deductible)}`
                },
            };
        });

        return Object.assign({}, ...vehicleCoverageOverride);
    };

    const generatePropertyOverrides = () => {
        const propertyCoverageOverride = filteredPropertyCoverages().map((propertyCoverage, index) => {
            return {
                [`propertyCoverageExposureAndIncident${index}`]: {
                    visible: !!(propertyCoverage.exposureLimit && propertyCoverage.incidentLimit),
                    content: `${formatCurrency(propertyCoverage.exposureLimit)}/${formatCurrency(propertyCoverage.incidentLimit)}`
                },
                [`propertyCoverageNoExpusureAndIncident${index}`]: {
                    visible: !!(!propertyCoverage.exposureLimit && propertyCoverage.incidentLimit),
                    content: `${formatCurrency(propertyCoverage.incidentLimit)}`
                },
                [`propertyCoverageExposureAndNoIncident${index}`]: {
                    visible: !!(propertyCoverage.exposureLimit && !propertyCoverage.incidentLimit),
                    content: `${formatCurrency(propertyCoverage.exposureLimit)}`
                },
                [`propertyCoverageDeductible${index}`]: {
                    visible: !!propertyCoverage.deductible,
                    content: `${formatCurrency(propertyCoverage.deductible)}`
                },
            };
        });

        return Object.assign({}, ...propertyCoverageOverride);
    };

    const overrides = {
        subsectionHeaderText: {
            content: <>{`${translator(messages.PolicyNumber)} ${claim?.policy?.policyNumber} ${claim?.policy?.baseState_ML}`}</>
        },
        policyCoverageCol: {
            visible: isPolicyCoverage()
        },
        policyCoverageIterable: {
            data: filteredPolicyCoverages()
        },
        vehicleCoverageCol: {
            visible: isPersonalAuto
        },
        vehicleCoverageIterable: {
            data: filteredVehicleCoverages()
        },
        propertyCoverageCol: {
            visible: !isPersonalAuto
        },
        propertyCoverageIterable: {
            data: filteredPropertyCoverages()
        },

        ...generatePolicyOverrides(),
        ...generateVehicleOverrides(),
        ...generatePropertyOverrides(),
    };
    const resolvers = {
        resolveClassNameMap: {},
        resolveComponentMap: {
        },
        resolveCallbackMap: {
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

export default MLClaimDetailsCoverages;
